import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, Loader } from 'lucide-react';
import {
  getCustomerUserStates,
  addCustomerUser,
  removeCustomerUser,
  listAssignableSharedConfigs,
  assignConfigToCustomer,
} from '../../../services/apiClient';
import { Button } from '../../../components/ui/button';
import UserRow from './UserRow';
import AssignConfigControl from './AssignConfigControl';
import UserAutocomplete from './UserAutocomplete';

/**
 * Users tab of the tenant admin console — lists a customer's users and lets
 * an admin add/remove users, inspect each user's dashboard state, and assign
 * a shared dashboard config to one user or to the whole customer at once.
 * Extracted from TenantAdminPage.jsx (Task 7); expanded with UserRow /
 * AssignConfigControl in Task 8.
 */
const UsersTab = ({ customerId }) => {
  const { t } = useTranslation('admin');
  const [users, setUsers] = useState([]);
  const [assignableConfigs, setAssignableConfigs] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const loadUsers = async (id) => {
    setLoading(true);
    setErr(null);
    try {
      setUsers(await getCustomerUserStates(id));
    } catch (e) {
      setErr(t('errors.listUsers', { detail: e.message }));
    } finally {
      setLoading(false);
    }
  };

  // Fetched once on mount, independently of (in parallel with) the
  // per-customer user-states fetch below — this list doesn't depend on
  // customerId, so it must not sit behind an awaited chain that would delay
  // rendering the users list.
  useEffect(() => {
    listAssignableSharedConfigs()
      .then(setAssignableConfigs)
      .catch((e) => setErr(t('errors.listAssignableConfigs', { detail: e.message })));
  }, []);

  useEffect(() => {
    if (customerId) {
      loadUsers(customerId);
    }
  }, [customerId]);

  const handleAssigned = (userId, configId, configName) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.user_id === userId
          ? { ...u, active_shared_config_id: configId, active_shared_config_name: configName }
          : u,
      ),
    );
  };

  const handleAutoActivateChanged = (userId, enabled) => {
    setUsers((prev) =>
      prev.map((u) => (u.user_id === userId ? { ...u, auto_activate_latest_etl_file: enabled } : u)),
    );
  };

  const handleAdd = async () => {
    if (!selectedUser || !customerId) return;
    setLoading(true);
    setErr(null);
    try {
      await addCustomerUser(customerId, selectedUser.email);
      setSelectedUser(null);
      setAutocompleteKey((k) => k + 1); // remount UserAutocomplete to clear its text/results
      await loadUsers(customerId);
    } catch (e) {
      setErr(t('errors.addUser', { detail: e.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (userId) => {
    setLoading(true);
    setErr(null);
    try {
      await removeCustomerUser(customerId, userId);
      await loadUsers(customerId);
    } catch (e) {
      setErr(t('errors.removeUser', { detail: e.message }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1">
      <h2 className="text-sm font-medium text-gray-500 mb-2">
        {t('tenants.usersFor', { customerId })}
      </h2>
      {err && <div className="mb-4 text-sm text-error-600">{err}</div>}
      {loading && <Loader className="w-4 h-4 animate-spin text-gray-400" />}
      <div className="mb-3">
        <AssignConfigControl
          label={t('assignConfig.assignToAll')}
          assignableConfigs={assignableConfigs}
          onAssign={(id, name) => assignConfigToCustomer(customerId, id, name).then(() => loadUsers(customerId))}
        />
      </div>
      <ul className="space-y-2 mb-4">
        {users.map((u) => (
          <UserRow
            key={u.user_id}
            user={u}
            customerId={customerId}
            assignableConfigs={assignableConfigs}
            onRemove={handleRemove}
            onAssigned={handleAssigned}
            onAutoActivateChanged={handleAutoActivateChanged}
          />
        ))}
      </ul>
      <div className="flex gap-2">
        <UserAutocomplete
          key={autocompleteKey}
          customerId={customerId}
          onSelectionChange={setSelectedUser}
          disabled={loading}
        />
        <Button onClick={handleAdd} disabled={loading || !selectedUser}>
          <UserPlus className="w-4 h-4 mr-1.5" /> {t('tenants.addUser')}
        </Button>
      </div>
    </div>
  );
};

export default UsersTab;
